import React from "react";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";

type TooltipArrowDirection = "up" | "right" | "down" | "left";

interface TooltipProps extends React.HTMLAttributes<HTMLDivElement> {
  text: string;
  iconName?: string;
  arrow?: TooltipArrowDirection;
}

interface TooltipHoverProps extends TooltipProps {
  children: React.ReactNode;
  offset?: number;
}

const TOOLTIP_WIDTH = 240;
const ARROW_SIZE = 18;
const DEFAULT_OFFSET = 12;

/**
 * Tooltip bubble that renders optional icon, text, and an arrow.
 */
export const Tooltip = ({
  text,
  iconName,
  arrow = "down",
  ...rest
}: TooltipProps): React.ReactElement => {
  const hasIcon = Boolean(iconName);

  return (
    <Container $hasIcon={hasIcon} {...rest}>
      {iconName && (
        <IconWrapper>
          <CustomIcon name={iconName} size="24px" color={ColorV2.Blue3} />
        </IconWrapper>
      )}
      <Text $hasIcon={hasIcon}>{text}</Text>
      <Arrow $direction={arrow} />
    </Container>
  );
};

/**
 * Wraps a trigger and shows the tooltip bubble on hover.
 */
export const TooltipHover = ({
  text,
  iconName,
  arrow = "down",
  offset = DEFAULT_OFFSET,
  children,
  ...rest
}: TooltipHoverProps): React.ReactElement => {
  return (
    <HoverWrap>
      <Trigger>{children}</Trigger>
      <Bubble $placement={arrow} $offset={offset}>
        <Tooltip text={text} iconName={iconName} arrow={arrow} {...rest} />
      </Bubble>
    </HoverWrap>
  );
};

const Container = styled.div<{ $hasIcon: boolean }>`
  ${TypographyV2.s1}
  position: relative;
  width: ${TOOLTIP_WIDTH}px;
  padding: 16px;
  border-radius: 16px;
  background-color: ${ColorV2.Blue6};
  color: ${ColorV2.Blue3};
  display: flex;
  gap: 16px;
  align-items: center;

  ${(props) =>
    !props.$hasIcon &&
    css`
      justify-content: center;
      text-align: center;
    `}
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const Text = styled.span<{ $hasIcon: boolean }>`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue3};
  ${(props) =>
    props.$hasIcon &&
    css`
      text-align: left;
    `}
`;

const Arrow = styled.span<{ $direction: TooltipArrowDirection }>`
  position: absolute;
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  background-color: ${ColorV2.Blue6};
  transform: rotate(45deg);

  ${(props) =>
    props.$direction === "up" &&
    css`
      top: -${ARROW_SIZE / 2}px;
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "down" &&
    css`
      bottom: -${ARROW_SIZE / 2}px;
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "left" &&
    css`
      left: -${ARROW_SIZE / 2}px;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "right" &&
    css`
      right: -${ARROW_SIZE / 2}px;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
    `}
`;

const Bubble = styled.div<{
  $placement: TooltipArrowDirection;
  $offset: number;
}>`
  position: absolute;
  z-index: 1000;
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  transform: translate(var(--tx), var(--ty));

  ${(props) =>
    props.$placement === "down" &&
    css`
      bottom: calc(100% + ${props.$offset}px);
      left: 50%;
      --tx: -50%;
      --ty: 4px;
      --tx-hover: -50%;
      --ty-hover: 0;
    `}

  ${(props) =>
    props.$placement === "up" &&
    css`
      top: calc(100% + ${props.$offset}px);
      left: 50%;
      --tx: -50%;
      --ty: -4px;
      --tx-hover: -50%;
      --ty-hover: 0;
    `}

  ${(props) =>
    props.$placement === "left" &&
    css`
      left: calc(100% + ${props.$offset}px);
      top: 50%;
      --tx: 4px;
      --ty: -50%;
      --tx-hover: 0;
      --ty-hover: -50%;
    `}

  ${(props) =>
    props.$placement === "right" &&
    css`
      right: calc(100% + ${props.$offset}px);
      top: 50%;
      --tx: -4px;
      --ty: -50%;
      --tx-hover: 0;
      --ty-hover: -50%;
    `}
`;

const HoverWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  width: fit-content;
  max-width: 100%;

  &:hover ${Bubble} {
    opacity: 1;
    transform: translate(var(--tx-hover), var(--ty-hover));
    pointer-events: auto;
  }
`;

const Trigger = styled.span`
  display: inline-flex;
  align-items: center;
`;
